/**
 * Global authentication interceptor for handling session expiration across the app
 */

let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

/**
 * Check if the error response indicates session expiration
 */
function isAuthError(response: Response): boolean {
	return (
		response.status === 401 ||
		(response.status === 500 &&
			(response.headers
				.get("content-type")
				?.includes("application/json") ??
				false))
	);
}

/**
 * Check if the response has session warning headers
 */
function hasSessionWarning(response: Response): boolean {
	return response.headers.get("X-Session-Warning") === "near-expiry";
}

/**
 * Refresh the current session by redirecting to the current page
 * This will trigger the middleware to validate and potentially refresh the session
 */
async function refreshSession(): Promise<void> {
	if (isRefreshing) {
		return refreshPromise || Promise.resolve();
	}

	isRefreshing = true;
	refreshPromise = new Promise((resolve) => {
		// Use a short delay to batch multiple simultaneous refresh attempts
		setTimeout(() => {
			if (typeof window !== "undefined") {
				// Reload the page to refresh the session through middleware
				window.location.reload();
			}
			resolve();
		}, 100);
	});

	try {
		await refreshPromise;
	} finally {
		isRefreshing = false;
		refreshPromise = null;
	}
}

/**
 * Handle authentication errors by attempting session refresh
 */
async function handleAuthError(
	originalFetch: typeof fetch,
	input: string | URL | Request,
	init: RequestInit = {},
): Promise<Response> {
	try {
		await refreshSession();

		// Retry the original request after session refresh
		const retryResponse = await originalFetch(input, init);

		// If still failing after refresh, the session is truly expired
		if (isAuthError(retryResponse)) {
			// Redirect to login page
			if (typeof window !== "undefined") {
				const currentPath = window.location.pathname;
				const redirectUrl = `/auth/login?redirectTo=${encodeURIComponent(currentPath)}`;
				window.location.href = redirectUrl;
			}
		}

		return retryResponse;
	} catch (error) {
		// If refresh fails, redirect to login
		if (typeof window !== "undefined") {
			const currentPath = window.location.pathname;
			const redirectUrl = `/auth/login?redirectTo=${encodeURIComponent(currentPath)}`;
			window.location.href = redirectUrl;
		}
		throw error;
	}
}

/**
 * Check if the request is a Next.js internal request or static asset that should be skipped
 */
function isNextJsInternalRequest(input: string | URL | Request): boolean {
	const url =
		typeof input === "string"
			? input
			: input instanceof URL
				? input.href
				: input.url;
	return (
		// better-auth answers failed sign-ins (for example a passkey) with 401;
		// those are not expired sessions and the caller shows the error.
		url.includes("/api/auth/") ||
		url.includes("__nextjs_") ||
		url.includes("_next/") ||
		url.includes("/audio/") ||
		url.includes("/images/") ||
		url.endsWith(".wav") ||
		url.endsWith(".mp3") ||
		url.endsWith(".png") ||
		url.endsWith(".jpg") ||
		url.endsWith(".svg")
	);
}

/**
 * Check if the request is a Next.js server action call
 * Server actions use special headers that we should not intercept
 */
function isServerActionRequest(
	input: string | URL | Request,
	init?: RequestInit,
): boolean {
	const headers = init?.headers;
	if (headers) {
		// Check for Next-Action header (used by server actions)
		if (headers instanceof Headers) {
			return headers.has("Next-Action");
		}
		if (Array.isArray(headers)) {
			return headers.some(([key]) => key.toLowerCase() === "next-action");
		}
		if (typeof headers === "object") {
			return "Next-Action" in headers || "next-action" in headers;
		}
	}
	return false;
}

/**
 * Enhanced fetch function with authentication error handling
 */
export function createAuthenticatedFetch(): typeof fetch {
	const originalFetch = global.fetch;

	return async function authenticatedFetch(
		input: string | URL | Request,
		init?: RequestInit,
	): Promise<Response> {
		// Skip interception for Next.js internal requests and server actions
		if (
			isNextJsInternalRequest(input) ||
			isServerActionRequest(input, init)
		) {
			return originalFetch(input, init);
		}
		const response = await originalFetch(input, init);

		// Check for session warning headers and log them
		if (hasSessionWarning(response)) {
			const expiresAt = response.headers.get("X-Session-Expires-At");
			console.warn(
				`Session will expire at ${expiresAt}. Consider refreshing soon.`,
			);
		}

		// Handle authentication errors
		if (isAuthError(response)) {
			// Check if this is a JSON response with our session expired code
			const contentType = response.headers.get("content-type");
			if (contentType?.includes("application/json")) {
				try {
					const errorData = await response.clone().json();
					if (
						errorData.code === "SESSION_EXPIRED" ||
						response.status === 401
					) {
						return handleAuthError(originalFetch, input, init);
					}
				} catch {
					// If we can't parse the JSON, treat as regular auth error
					if (response.status === 401) {
						return handleAuthError(originalFetch, input, init);
					}
				}
			}
		}

		return response;
	};
}

/**
 * Install the global authentication interceptor
 * This should be called early in the app initialization
 */
export function installAuthInterceptor(): void {
	if (typeof window !== "undefined") {
		// Replace global fetch with our authenticated version
		const authenticatedFetch = createAuthenticatedFetch();
		global.fetch = authenticatedFetch;

		console.log("🔒 Authentication interceptor installed");
	}
}

/**
 * Manual session refresh utility for components that need it
 */
export function requestSessionRefresh(): Promise<void> {
	return refreshSession();
}
